import { Context } from '@nuxt/types';
import {
  reactive,
  Ref,
  ref,
} from '@nuxtjs/composition-api';
import {
  LaneType, laneTypes, ToDoListItem, TodoListRequest,
} from '~/types/Todo';

export interface TodoState {
  isfirstTimeToFetch: Ref<boolean>;
  chatLaneTodoList: Ref<ToDoListItem[]>;
  inquiryLaneTodoList: Ref<ToDoListItem[]>;
  reservationLaneTodoList: Ref<ToDoListItem[]>;
  otherLaneTodoList: Ref<ToDoListItem[]>;
  fetchTodoList: (isFetchMore?: boolean) => Promise<void>;
  todoPageNum: Ref<number>;
  todoLists: {
    chat: Ref<ToDoListItem[]>;
    inquiry: Ref<ToDoListItem[]>;
    reservation: Ref<ToDoListItem[]>;
    other: Ref<ToDoListItem[]>;
  };
  resetTodoList: () => void;
}

export const useTodoState = (context: Context): TodoState => {
  const isfirstTimeToFetch = ref<boolean>(true);
  const chatLaneTodoList = ref<ToDoListItem[]>([]);
  const inquiryLaneTodoList = ref<ToDoListItem[]>([]);
  const reservationLaneTodoList = ref<ToDoListItem[]>([]);
  const otherLaneTodoList = ref<ToDoListItem[]>([]);
  const todoPageNum = ref<number>(1);
  const todoPageNums = reactive<Record<LaneType, number>>({
    [LaneType.Chat]: 0,
    [LaneType.Inquiry]: 0,
    [LaneType.Reservation]: 0,
    [LaneType.Other]: 0,
  });

  const hasMoreData = reactive<Record<LaneType, boolean>>({
    [LaneType.Chat]: true,
    [LaneType.Inquiry]: true,
    [LaneType.Reservation]: true,
    [LaneType.Other]: true,
  });

  // laneTypeごとのデータ格納Refのマップ
  const todoLists = {
    [LaneType.Chat]: chatLaneTodoList,
    [LaneType.Inquiry]: inquiryLaneTodoList,
    [LaneType.Reservation]: reservationLaneTodoList,
    [LaneType.Other]: otherLaneTodoList,
  };

  const PAGE_LIMIT = 10;
  const fetchTodoList = async (isFetchMore: boolean = false): Promise<void> => {
    const requests = laneTypes.map((laneType) => {
      if (!hasMoreData[laneType]) return Promise.resolve(null);
      if (!isFetchMore) return Promise.resolve(null);
      const param: TodoListRequest = {
        limit: PAGE_LIMIT,
        page: todoPageNums[laneType],
        laneType,
      };
      return context.$api.getTodoList(param);
    });

    const results = await Promise.allSettled(requests);

    results.forEach((result, index) => {
      const laneType = laneTypes[index];
      const listRef = todoLists[laneType];

      if (result.status === 'fulfilled' && result.value) {
        const items = result.value.todoList || [];
        listRef.value.push(...items);
        if (items.length < PAGE_LIMIT) {
          hasMoreData[laneType] = false;
        } else {
          todoPageNums[laneType] += 1;
        }
      }
    });
  };

  const resetTodoList = (): void => {
    Object.keys(todoLists).forEach((laneType) => {
      todoLists[laneType].value = [];
      hasMoreData[laneType] = true;
      todoPageNums[laneType] = 0;
    });
  };

  return {
    isfirstTimeToFetch,
    chatLaneTodoList,
    inquiryLaneTodoList,
    reservationLaneTodoList,
    otherLaneTodoList,
    fetchTodoList,
    todoLists,
    todoPageNum,
    resetTodoList,
  };
};
